import DigiobcanApi, {
  type IssueStatement,
  type IssueStatementMedia,
  type StatementRevision,
} from '../../../../api/DigiobcanApi';
import { useM } from '../../../../lib/query';
import { useLogger } from '../../../../providers/LoggerProvider';
import { __, toastError, toastSuccess } from '../../../../utils';
import { FRAME_SX } from './StatementApproval';
import {
  Button,
  Stack,
  TextField,
  Typography,
} from '@presov.sk/presov-ui-react';
import React from 'react';

type Props = {
  /** Vyjadrenie, ktorého úprava je v schvaľovaní. */
  statement: IssueStatement;
  /** Úprava v schvaľovaní - `approval` je `pending` alebo `rejected`. */
  revision: StatementRevision;
  /**
   * Smie prihlásený o čakajúcej úprave rozhodnúť? Ako pri vyjadrení to počíta
   * server (`awaiting_my_approval` na náleze, zahŕňa aj úpravy), aby sa
   * tlačidlá nerozišli s filtrom „Na schválenie".
   */
  canReview: boolean;
  /** Smie vrátenú úpravu stiahnuť (autor, schvaľovateľ, správca)? */
  canWithdraw: boolean;
  /** `requires_approval` prihláseného - rozhoduje o tom, čo urobí „Poslať znova". */
  requiresApproval: boolean;
  /** Podnet je archivovaný alebo kategória nie je prihláseného. */
  readOnly?: boolean;
  compact?: boolean;
  onDone: () => Promise<unknown>;
};

type Action = 'approve' | 'reject' | 'resubmit' | 'withdraw';

const SUCCESS: Record<Action, (published: boolean) => string> = {
  approve: () => __('Úprava bola schválená a zverejnená.'),
  reject: () => __('Úprava bola vrátená autorovi.'),
  resubmit: (published) =>
    published
      ? __('Schválenie už nepotrebujete - úprava bola zverejnená.')
      : __('Úprava bola znova odoslaná na schválenie.'),
  withdraw: () => __('Úprava bola stiahnutá.'),
};

const MediaLinks: React.FC<{
  media: IssueStatementMedia[];
  prefix: string;
  struck?: boolean;
}> = ({ media, prefix, struck = false }) => (
  <>
    {media.map((item) => (
      <Typography
        key={item.media_id}
        variant='caption'
        component='a'
        href={item.public_url}
        target='_blank'
        rel='noopener noreferrer'
        sx={{ textDecoration: struck ? 'line-through' : undefined }}
      >
        {`${prefix} ${item.original_filename ?? item.media_id}`}
      </Typography>
    ))}
  </>
);

/**
 * Úprava zverejneného vyjadrenia v schvaľovaní (moj-presov#18). Tie isté
 * tri pohľady ako [StatementApproval](StatementApproval.tsx) - schvaľovateľ,
 * autor vrátenej a ostatní - s dvoma rozdielmi, ktoré menia texty:
 *
 *  * občan medzitým **vidí doterajšie znenie**, nie nič - preto blok ukazuje
 *    oboje, doterajšie aj navrhované,
 *  * úprava **nález neblokuje**, zablokovaná je len ďalšia úprava toho istého
 *    vyjadrenia. Riešiť nález sa dá ďalej.
 */
const StatementRevisionApproval: React.FC<Props> = ({
  statement,
  revision,
  canReview,
  canWithdraw,
  requiresApproval,
  readOnly = false,
  compact = false,
  onDone,
}) => {
  const { logger } = useLogger();
  const [text, setText] = React.useState(revision.text);
  const [note, setNote] = React.useState('');

  React.useEffect(() => {
    setText(revision.text);
    setNote('');
  }, [revision.revision_id, revision.text, revision.approval]);

  const rejected = revision.approval === 'rejected';
  const lastReview = revision.reviews[revision.reviews.length - 1];
  const edited = text.trim() !== revision.text;

  const mutation = useM(
    async (action: Action) => {
      const id = revision.revision_id;
      let published = false;

      if (action === 'resubmit') {
        const res = await DigiobcanApi.adminResubmitStatementRevision(
          id,
          {},
          {
            text: text.trim(),
            ...(revision.media_removed.length && {
              removed_media_ids: revision.media_removed.map((m) => m.media_id),
            }),
          },
        );
        published = !!res.published;
      } else if (action === 'withdraw') {
        await DigiobcanApi.adminWithdrawStatementRevision(id);
      } else {
        await DigiobcanApi.adminReviewStatementRevision(
          id,
          {},
          {
            decision: action,
            ...(action === 'approve' && edited && { text: text.trim() }),
            ...(note.trim() && { note: note.trim() }),
          },
        );
      }

      await onDone();
      return { action, published };
    },
    {
      onSuccess: ({ action, published }) =>
        toastSuccess(SUCCESS[action](published)),
      onError: (error, action) => {
        logger.error('Failed to change statement revision in review', {
          action: 'statementRevisionReview',
          error,
          data: { revisionId: revision.revision_id, change: action },
        });

        const name = error.details?.name;
        if (
          name === 'STATEMENT_NOT_PENDING' ||
          name === 'STATEMENT_NOT_REJECTED' ||
          name === 'STATEMENT_MEDIA_NOT_FOUND'
        ) {
          toastError(
            __('S úpravou už medzitým niekto niečo urobil. Obnovte stránku.'),
          );
        } else if (name === 'FORBIDDEN_STATEMENT_APPROVAL') {
          toastError(__('Vyjadrenia v tejto kategórii neschvaľujete.'));
        } else if (name === 'REPORT_ARCHIVED') {
          toastError(__('Podnet je archivovaný.'));
        } else {
          toastError(__('Zmenu sa nepodarilo uložiť.'));
        }
      },
    },
  );

  const busy = mutation.isPending;

  const buttonsSx = {
    direction: compact ? ('column' as const) : ('row' as const),
    alignItems: compact ? 'stretch' : 'center',
  };

  const mediaChanges = (revision.media_added.length > 0 ||
    revision.media_removed.length > 0) && (
    <Stack gap={0.25}>
      <MediaLinks media={revision.media_added} prefix='+' />
      <MediaLinks media={revision.media_removed} prefix='-' struck />
    </Stack>
  );

  // Doterajšie znenie - to, čo občan práve vidí. Bez neho by schvaľovateľ
  // posudzoval úpravu bez toho, čo upravuje.
  const currentText = (
    <Stack gap={0.25}>
      <Typography variant='caption' color='warning.dark'>
        {__('Občan zatiaľ vidí')}
      </Typography>
      <Typography
        variant='body2'
        color='text.secondary'
        sx={{
          p: 1.25,
          borderRadius: 1,
          backgroundColor: 'background.paper',
          whiteSpace: 'pre-line',
        }}
      >
        {statement.text}
      </Typography>
    </Stack>
  );

  const proposedText = (
    <Stack
      gap={0.5}
      sx={{ p: 1.25, borderRadius: 1, backgroundColor: 'background.paper' }}
    >
      <Typography variant='body2' sx={{ whiteSpace: 'pre-line' }}>
        {revision.text}
      </Typography>
      {mediaChanges}
    </Stack>
  );

  const returnedNote = rejected && lastReview?.note && (
    <Typography
      variant='body2'
      sx={{
        px: 1.25,
        py: 0.75,
        borderRadius: 1,
        borderLeft: (theme) => `3px solid ${theme.palette.warning.main}`,
        backgroundColor: 'background.paper',
      }}
    >
      {lastReview.note}
    </Typography>
  );

  // ── Autor prepracúva vrátenú ──────────────────────────────────────────────
  if (rejected && revision.mine && !readOnly) {
    return (
      <Stack gap={1.25} sx={FRAME_SX}>
        <Stack gap={0.25}>
          <Typography variant='body2' color='warning.dark' fontWeight={700}>
            {__('Úpravu vyjadrenia vám vrátili')}
          </Typography>
          <Typography variant='caption' color='warning.dark'>
            {requiresApproval
              ? __(
                  'Prepracujte ju podľa pripomienky a pošlite znova. Občan zatiaľ vidí doterajšie znenie.',
                )
              : __(
                  'Schválenie už nepotrebujete - po odoslaní sa úprava hneď zverejní.',
                )}
          </Typography>
        </Stack>

        {returnedNote}
        {currentText}

        <TextField
          multiline
          minRows={compact ? 4 : 3}
          fullWidth
          value={text}
          onChange={(event) => setText(event.target.value)}
          sx={{ backgroundColor: 'background.paper' }}
        />
        {mediaChanges}

        <Stack gap={1} {...buttonsSx}>
          <Button
            fullWidth={compact}
            disabled={busy || !text.trim()}
            onClick={() => mutation.mutate('resubmit')}
          >
            {requiresApproval
              ? __('Odoslať znova na schválenie')
              : __('Zverejniť úpravu')}
          </Button>
          <Button
            variant='text'
            fullWidth={compact}
            disabled={busy}
            onClick={() => mutation.mutate('withdraw')}
          >
            {__('Stiahnuť úpravu')}
          </Button>
        </Stack>
      </Stack>
    );
  }

  // ── Vrátená, pohľad ostatných ─────────────────────────────────────────────
  if (rejected) {
    return (
      <Stack gap={0.75} sx={FRAME_SX}>
        <Typography variant='body2' color='warning.dark' fontWeight={500}>
          {__('Úprava vyjadrenia je vrátená autorovi')}
        </Typography>
        <Typography variant='caption' color='warning.dark'>
          {__(
            'Kým ju autor neprepracuje alebo nestiahne, vyjadrenie sa nedá upraviť znova. Nález sa dá riešiť ďalej.',
          )}
        </Typography>
        {proposedText}
        {returnedNote}
        {canWithdraw && !readOnly && (
          <Stack gap={1} {...buttonsSx}>
            <Button
              variant='text'
              fullWidth={compact}
              disabled={busy}
              onClick={() => mutation.mutate('withdraw')}
            >
              {__('Stiahnuť úpravu')}
            </Button>
          </Stack>
        )}
      </Stack>
    );
  }

  const title = statement.final
    ? __('Úprava finálneho vyjadrenia čaká na schválenie')
    : __('Úprava vyjadrenia čaká na schválenie');

  // ── Čaká, pohľad toho, kto neschvaľuje ────────────────────────────────────
  if (!canReview || readOnly) {
    return (
      <Stack gap={0.75} sx={FRAME_SX}>
        <Typography variant='body2' color='warning.dark' fontWeight={500}>
          {title}
        </Typography>
        <Typography variant='caption' color='warning.dark'>
          {revision.mine
            ? __(
                'Občan uvidí nové znenie až po schválení, dovtedy vidí doterajšie.',
              )
            : __(
                'Schváliť ju môže schvaľovateľ alebo správca kategórie. Dovtedy občan vidí doterajšie znenie.',
              )}
        </Typography>
        {proposedText}
      </Stack>
    );
  }

  // ── Čaká, schvaľovateľ rozhoduje ──────────────────────────────────────────
  return (
    <Stack gap={1.25} sx={FRAME_SX}>
      <Stack gap={0.25}>
        <Typography variant='body2' color='warning.dark' fontWeight={700}>
          {title}
        </Typography>
        <Typography variant='caption' color='warning.dark'>
          {__(
            'Schválením sa nové znenie zverejní pri podnete so štítkom „upravené" a občan dostane správu.',
          )}
        </Typography>
      </Stack>

      {currentText}

      <TextField
        multiline
        minRows={compact ? 4 : 3}
        fullWidth
        value={text}
        onChange={(event) => setText(event.target.value)}
        helperText={
          edited
            ? __('Zverejní sa vaše znenie, autorovo ostane v histórii.')
            : undefined
        }
        sx={{ backgroundColor: 'background.paper' }}
      />
      {mediaChanges}

      <TextField
        multiline
        minRows={2}
        fullWidth
        value={note}
        onChange={(event) => setNote(event.target.value)}
        placeholder={__(
          'Pripomienka pre autora (nepovinné pri schválení, občan ju neuvidí)…',
        )}
        sx={{ backgroundColor: 'background.paper' }}
      />

      <Stack gap={1} {...buttonsSx}>
        <Button
          fullWidth={compact}
          disabled={busy || !text.trim()}
          onClick={() => mutation.mutate('approve')}
        >
          {edited ? __('Upraviť a schváliť') : __('Schváliť')}
        </Button>
        <Button
          variant='text'
          fullWidth={compact}
          disabled={busy}
          onClick={() => mutation.mutate('reject')}
        >
          {__('Vrátiť autorovi')}
        </Button>
      </Stack>
    </Stack>
  );
};

export default StatementRevisionApproval;
